import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { Stepper, type StepperStep } from '@/components/stepper/Stepper'
import type { TExhibitorWithEntries } from '@schemas'

type CheckInStepperContextValue = {
  activeStepId: string
  goToStep: (stepId: string) => void
  goToNext: () => void
  goToPrevious: () => void
  selectedEntrant: TExhibitorWithEntries | null
  setSelectedEntrant: (entrant: TExhibitorWithEntries | null) => void
}

const CheckInStepperContext = createContext<CheckInStepperContextValue | null>(null)

export function useCheckInStepperContext(): CheckInStepperContextValue {
  const value = useContext(CheckInStepperContext)
  if (!value) {
    throw new Error('useCheckInStepperContext must be used within CheckInStepper')
  }
  return value
}

type CheckInStep = StepperStep & {
  renderFooter?: (context: CheckInStepperContextValue) => ReactNode
}

type CheckInStepperProps = {
  steps: Array<CheckInStep | null>
  initialStepId?: string
  onStepChange?: (stepId: string) => void
  initialEntrant?: TExhibitorWithEntries | null
  onEntrantChange?: (entrant: TExhibitorWithEntries | null) => void
}

export function CheckInStepper({
  steps,
  initialStepId,
  onStepChange,
  initialEntrant,
  onEntrantChange,
}: CheckInStepperProps) {
  const normalizedSteps = useMemo<CheckInStep[]>(
    () => steps.filter((step): step is CheckInStep => Boolean(step)),
    [steps]
  )

  const defaultStepId = useMemo(() => {
    if (initialStepId) {
      const match = normalizedSteps.find(step => step.id === initialStepId)
      if (match) return match.id
    }
    return normalizedSteps[0]?.id ?? ''
  }, [initialStepId, normalizedSteps])

  const [activeStepId, setActiveStepId] = useState(defaultStepId)
  const [selectedEntrant, setSelectedEntrantState] = useState<TExhibitorWithEntries | null>(
    initialEntrant ?? null
  )
  const selectedEntrantRef = useRef<TExhibitorWithEntries | null>(initialEntrant ?? null)
  const onEntrantChangeRef = useRef(onEntrantChange)
  const previousStepIdRef = useRef(activeStepId)
  const lastNotifiedStepIdRef = useRef<string | null>(null)
  const hasMountedRef = useRef(false)

  const setActiveStepIdInternal = useCallback((nextStepId: string) => {
    setActiveStepId(previous => (previous === nextStepId ? previous : nextStepId))
  }, [])

  useEffect(() => {
    if (!activeStepId && defaultStepId) {
      setActiveStepIdInternal(defaultStepId)
      return
    }

    const exists = normalizedSteps.some(step => step.id === activeStepId)
    if (!exists) {
      setActiveStepIdInternal(defaultStepId)
    }
  }, [activeStepId, defaultStepId, normalizedSteps, setActiveStepIdInternal])

  useEffect(() => {
    if (!initialStepId) return
    const match = normalizedSteps.find(step => step.id === initialStepId)
    if (!match) return
    setActiveStepIdInternal(match.id)
  }, [initialStepId, normalizedSteps, setActiveStepIdInternal])

  useEffect(() => {
    const next = initialEntrant ?? null
    selectedEntrantRef.current = next
    setSelectedEntrantState(next)
  }, [initialEntrant])

  useEffect(() => {
    onEntrantChangeRef.current = onEntrantChange
  }, [onEntrantChange])

  // Steps call this after awaits, so notify the parent's current handler rather
  // than the one captured when the step rendered.
  const setSelectedEntrant = useCallback((entrant: TExhibitorWithEntries | null) => {
    if (selectedEntrantRef.current === entrant) return
    selectedEntrantRef.current = entrant
    setSelectedEntrantState(entrant)
    onEntrantChangeRef.current?.(entrant)
  }, [])

  const activeIndex = useMemo(() => {
    if (normalizedSteps.length === 0) return 0
    const index = normalizedSteps.findIndex(step => step.id === activeStepId)
    return index >= 0 ? index : 0
  }, [activeStepId, normalizedSteps])

  const activeStep = normalizedSteps[activeIndex]

  const goToIndex = (index: number) => {
    const next = normalizedSteps[index]
    if (next) {
      setActiveStepIdInternal(next.id)
    }
  }

  const handleNext = () => {
    goToIndex(activeIndex + 1)
  }

  const handleBack = () => {
    goToIndex(activeIndex - 1)
  }

  const contextValue = useMemo<CheckInStepperContextValue>(
    () => ({
      activeStepId,
      goToStep: stepId => {
        const index = normalizedSteps.findIndex(step => step.id === stepId)
        if (index >= 0) {
          goToIndex(index)
        }
      },
      goToNext: handleNext,
      goToPrevious: handleBack,
      selectedEntrant,
      setSelectedEntrant,
    }),
    [activeStepId, handleBack, handleNext, normalizedSteps, selectedEntrant, setSelectedEntrant]
  )

  useEffect(() => {
    if (previousStepIdRef.current !== activeStepId) {
      const firstStepId = normalizedSteps[0]?.id
      const isReturningToFirst = firstStepId && activeStepId === firstStepId
      const cameFromOtherStep =
        previousStepIdRef.current && previousStepIdRef.current !== firstStepId
      if (isReturningToFirst && cameFromOtherStep) {
        setSelectedEntrant(null)
      }
      previousStepIdRef.current = activeStepId
    }
  }, [activeStepId, normalizedSteps, setSelectedEntrant])

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true
      lastNotifiedStepIdRef.current = activeStepId
      return
    }
    if (!activeStepId || lastNotifiedStepIdRef.current === activeStepId) {
      return
    }
    lastNotifiedStepIdRef.current = activeStepId
    onStepChange?.(activeStepId)
  }, [activeStepId, onStepChange])

  const mappedSteps = useMemo<StepperStep[]>(
    () =>
      normalizedSteps.map(step => ({
        id: step.id,
        label: step.label,
        description: step.description,
        content: step.content,
        renderFooter: step.renderFooter ? () => step.renderFooter?.(contextValue) : undefined,
      })),
    [contextValue, normalizedSteps]
  )

  if (!activeStep) {
    return null
  }

  return (
    <CheckInStepperContext.Provider value={contextValue}>
      <Stepper
        steps={mappedSteps}
        activeStepId={activeStep.id}
        initialStepId={defaultStepId}
        onStepChange={setActiveStepIdInternal}
        ariaLabel="Check-In steps"
        testIdPrefix="check-in-step"
      />
    </CheckInStepperContext.Provider>
  )
}
